import React, {useCallback, useEffect, useReducer, useRef} from "react";
import Question, {QuestionAnswerResultBlock} from "./Question";
import axios from "axios";
import {CheckCircleOutlined, CloseCircleOutlined} from "@ant-design/icons";
import {Button, Row} from "antd";
import TrailFeedback from "./TrailFeedback";
import CircularLoader from "@common/CircularLoader";
import Card from "@common/Card";
import {RESULT_VIEWS} from "./questionTypes/DragAndDropQuestion";

const ACTIONS = {
    LOAD_QUESTION: "LOAD_QUESTION",
    LOAD_QUESTION_SUCCESS: "LOAD_QUESTION_SUCCESS",
    UPDATE_SUBMITTABLE: "UPDATE_SUBMITTABLE",
    SUBMIT_QUESTION: "SUBMIT_QUESTION",
    SUBMIT_QUESTION_SUCCESS: "SUBMIT_QUESTION_SUCCESS",
    AJAX_ERROR: "AJAX_ERROR",
    UPDATE_DND_RESULTS_VIEW: "UPDATE_DND_RESULTS_VIEW",
}

const questionsInitialState = {
    isLoading: true,
    error: null,
    questionLevel: null,
    question: null,
    questionSubmittable: false,
    questionAnswered: false,
    questionResult: null,
    dndResultView: RESULT_VIEWS.MY_ANSWERS,
}
const resetQuestionState = {
    error: questionsInitialState.error,
    questionSubmittable: questionsInitialState.questionSubmittable,
    questionAnswered: questionsInitialState.questionAnswered,
    questionResult: questionsInitialState.questionResult,
    dndResultView: questionsInitialState.dndResultView,
};

function questionsReducer(state, action) {
    if (action.type === ACTIONS.LOAD_QUESTION) {
        return {...state, isLoading: true, question: null, ...resetQuestionState};
    } else if (action.type === ACTIONS.LOAD_QUESTION_SUCCESS) {
        return {
            ...state,
            isLoading: false,
            question: action.payload.question,
            questionLevel: action.payload.questionLevel,
        };
    } else if (action.type === ACTIONS.UPDATE_SUBMITTABLE) {
        return {...state, questionSubmittable: action.payload};
    } else if (action.type === ACTIONS.SUBMIT_QUESTION) {
        return {...state, questionAnswered: true};
    } else if (action.type === ACTIONS.SUBMIT_QUESTION_SUCCESS) {
        return {...state, questionResult: action.payload};
    } else if (action.type === ACTIONS.AJAX_ERROR) {
        return {...state, isLoading: false, error: action.payload};
    } else if (action.type === ACTIONS.UPDATE_DND_RESULTS_VIEW) {
        return {...state, dndResultView: action.payload};
    } else {
        return state;
    }
}

const TrailQuestions = props => {
    const {step, stepUserResult, setStepUserResult, continueCurrentStep} = props;
    const [state, dispatch] = useReducer(questionsReducer, questionsInitialState);
    const answerRef = useRef("");
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (!step) return;

        const controller = new AbortController();
        dispatch({type: ACTIONS.LOAD_QUESTION});
        const loadQuestion = async () => {
            try {
                const url = `/web/ui/question/trail/load`;
                const {data} = await axios.get(url, {
                    params: {pathwayUserId: step.pathwayUserId},
                    signal: controller.signal,
                });
                dispatch({type: ACTIONS.LOAD_QUESTION_SUCCESS, payload: {question: data, questionLevel: stepUserResult.activityLevel}});
            } catch (error) {
                if (axios.isCancel(error)) return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Failed to load question'});
            }
        };

        loadQuestion();

        return () => controller.abort();
    }, [step, stepUserResult]);

    const loadStepResults = useCallback(async (signal) => {
        try {
            const {data} = await axios.get('/web/ui/trail/step/userResult', {
                params: {pathwayUserId: step.pathwayUserId, stepId: step.stepId},
                signal: signal,
            });
            if (data.complete) {
                continueCurrentStep();
            } else {
                setStepUserResult(data);
            }
        } catch(error) {
            if (axios.isCancel(error)) return;
            dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Failed to load step results'});
        }
    }, [step, continueCurrentStep, setStepUserResult]);

    const handleUpdateQuestionData = useCallback((questionAnswers, isSubmittable) => {
        answerRef.current = questionAnswers;
        dispatch({type: ACTIONS.UPDATE_SUBMITTABLE, payload: isSubmittable});
    }, []);

    const handleSubmit = event => {
        event.preventDefault();

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        dispatch({type: ACTIONS.SUBMIT_QUESTION});

        const submitQuestion = async () => {
            try {
                const answerBody = answerRef.current;
                const {data} = await axios.post('/web/ui/question/answer',
                    answerBody,
                    {
                        params: {
                            conceptId: stepUserResult.conceptId,
                            level: stepUserResult.activityLevel,
                            questionId: state.question.questionId
                        },
                        signal: abortControllerRef.current.signal
                    }
                );
                dispatch({type: ACTIONS.SUBMIT_QUESTION_SUCCESS, payload: data});
            } catch (error) {
                if (axios.isCancel(error)) return;
                dispatch({type: ACTIONS.AJAX_ERROR, payload: 'Error submitting question'});
            }
        }

        submitQuestion();
    }

    const handleContinue = () => {
        loadStepResults();
    }

    return (
        <Card variant={"outlined"}>
            {state.isLoading && (
                <Row>
                    <CircularLoader displayInElement={true}/>
                </Row>
            )}
            {!state.isLoading && (
                <>
                    <form
                        className="ch-q-form"
                        onSubmit={handleSubmit}
                    >
                        <Question
                            question={state.question}
                            isLoading={state.question === null}
                            readOnly={state.questionAnswered}
                            updateQuestionData={handleUpdateQuestionData}
                            {...state.questionResult && {
                                result: state.questionResult,
                                dndResultView: state.dndResultView,
                            }}
                            displayAnswerOnResult={false}
                        />
                        {!state.questionAnswered && (
                            <Row justify="end">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!state.questionSubmittable}
                                >
                                    Answer
                                </Button>
                            </Row>
                        )}
                    </form>
                    {state.questionResult && (
                        <TrailFeedback
                            title={
                                <>
                                    {state.questionResult.result ? (
                                        <CheckCircleOutlined className="ch-feedback-icon" aria-hidden="true"/>
                                    ) : (
                                        <CloseCircleOutlined className="ch-feedback-icon" aria-hidden="true"/>
                                    )}
                                    <h3 className="ch-feedback-title">{state.questionResult.result ? 'Correct' : 'Incorrect'}</h3>
                                </>
                            }
                            buttons={
                                <>
                                    <Button
                                        type="primary"
                                        onClick={handleContinue}
                                    >
                                        Continue
                                    </Button>
                                </>
                            }
                        >
                            <QuestionAnswerResultBlock
                                result={state.questionResult}
                                questionAnswerType={state.question?.questionAnswerType}
                                inDialog={true}
                                dndValue={state.dndResultView}
                                dndOnChange={view => dispatch({type: ACTIONS.UPDATE_DND_RESULTS_VIEW, payload: view})}
                            />
                        </TrailFeedback>
                    )}
                </>
            )}
        </Card>
    )
}

export default TrailQuestions;